import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { applicationInsights } from '../../ApplicationInsights';
import { Header } from '../../common_v2/components/Header';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme } from '@apply/components';
import { DrawApi } from '@apply/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import '../scss/_reviewandsubmit.scss';
import { Signature } from '@apply/components/src/components/controls/Signature';
import { IDraw, IDrawBudget } from '@apply/api/src/api/DrawApi';
import { DrawType } from '@apply/constants';

interface DrawLine {
    label: string;
    value: number;
}

export const MobileReviewAndSubmit: React.FC = () => {
    const [drawLines, set_drawLines] = useState<DrawLine[]>([]);
    const [total, set_total] = useState<number>(0);
    const [street, set_street] = useState<string>('');
    const [city, set_city] = useState<string>('');
    const [drawBudget, set_drawBudget] = useState<IDrawBudget | undefined>(undefined);
    const [latestDraw, set_latestDraw] = useState<IDraw | undefined>(undefined);

    const drawApi = new DrawApi.DrawApi(applicationInsights);
    const drawBudgetId = parseInt(location.pathname.split('/')[2]);

    const loadData = async () => {
        const response = await drawApi.getDrawBudget(drawBudgetId);
        if (response.statusCode !== 200) return;

        const budgetResult = response.result;
        set_drawBudget(budgetResult);

        const latestDraw = budgetResult.draws[budgetResult.draws?.length - 1];
        set_latestDraw(latestDraw);

        const newDrawLines: DrawLine[] = [];
        budgetResult?.drawBudgetItems.map((budgetItem) => {
            if (budgetItem.drawItems) {
                const drawItem = budgetItem.drawItems.find(
                    (drawItem) => drawItem.drawNumber == budgetResult.drawCount - 1 && drawItem.borrowerRequest > 0
                );

                let value;
                if (latestDraw.borrowerSubmitted && latestDraw.needsBorrowerSignature) {
                    value = drawItem?.funded ?? 0;
                } else {
                    value = drawItem?.borrowerRequest ?? 0;
                }

                if (drawItem) newDrawLines.push({ label: budgetItem.description, value: value });
            }
        });
        set_drawLines(newDrawLines);

        const total = newDrawLines.reduce((sum, line) => sum + line.value, 0);
        set_total(total);

        const [address, city, state] = budgetResult?.propertyAddress.split(',') ?? [];

        set_street(address);
        set_city(city + ' ' + state);
    };

    useEffect(() => {
        loadData();
    }, [drawBudgetId]);

    const navigate = useNavigate();

    const drawSignPage = () => {
        navigate(`/mobiledrawsign/${drawBudgetId}`);
    };

    const drawControlPageOrHome = () => {
        if (!latestDraw || !drawBudget) return;

        if (
            latestDraw.drawType === DrawType.Automatic ||
            latestDraw.borrowerSubmitted ||
            !drawBudget.isEquityMet ||
            drawBudget.isBudgetMaxed ||
            !drawBudget.isApproved ||
            drawBudget.isFunded
        ) {
            navigate('/home');
            return;
        }

        navigate(`/draw/${drawBudgetId}`);
    };

    const submitButtonText = () => {
        if (!latestDraw) return '';

        if (latestDraw?.borrowerRequested && latestDraw.needsBorrowerSignature) {
            return 'Sign Revised Draw';
        }

        return 'Review and Submit';
    };

    return (
        <div>
            <Header />
            <div className='reviewandsubmit'>
                {latestDraw?.borrowerSubmitted ? (
                    <h2>{`Submitted on: ${latestDraw?.borrowerRequested}`}</h2>
                ) : (
                    <h2>Review and Submit</h2>
                )}
                <div className='number'>Draw #{drawBudget?.drawCount}</div>
                <Panel
                    theme={PanelTheme.White}
                    borderRadius={PanelBorderRadius.Medium}
                    padding={4}
                >
                    <div className='header'>
                        <div>
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className='icon'
                            />
                            <div className='address'>
                                <span className='street'>{street}</span>
                                <span className='city'>{city}</span>
                            </div>
                        </div>
                    </div>
                </Panel>
                <Panel
                    theme={PanelTheme.White}
                    borderRadius={PanelBorderRadius.Medium}
                    padding={8}
                >
                    <div className='body'>
                        {drawLines.map((dbi) => (
                            <div
                                key={dbi.label}
                                className='item'
                            >
                                <span className='label'>{dbi.label}</span>
                                <span className='value'>
                                    {dbi.value.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className='total'>
                        <span className='label'>Total Draw</span>
                        <span className='value'>
                            {total.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                        </span>
                    </div>
                </Panel>
                <div className='submit-button'>
                    <Button
                        id='primaryButton'
                        theme={ButtonTheme.LinkBlue}
                        width='250px'
                        handleClick={drawControlPageOrHome}
                    >
                        <span className='icon'>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </span>{' '}
                        &nbsp; Back
                    </Button>
                    {latestDraw?.borrowerSubmitted && !latestDraw.needsBorrowerSignature ? (
                        <Signature />
                    ) : (
                        <Button
                            id='primaryButton'
                            theme={ButtonTheme.Primary}
                            width='314px'
                            handleClick={drawSignPage}
                        >
                            {submitButtonText()}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
